import React from 'react';
import { useOrderTypeContext } from '../../Contexts/OrderTypeContext';
import CustomerService from '../../Services/CustomerService';
import ButtonPrimary from './ButtonPrimary';
import { TbCircleFilled } from 'react-icons/tb';
import { useLayoutContext } from '../../Contexts/LayoutContext';
import { Formik, Form, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import 'react-phone-input-2/lib/style.css'
import { useTranslation } from 'react-i18next';
import { useLoginHook } from '../../Hooks/LoginHook';
import { useParams } from 'react-router-dom';
import { ERROR_PASSWORD_RESET_NOT_FOUND } from '../../Utils/Constants/Login';

const getValidationSchema = (t: any) =>
    Yup.object().shape({
        password: Yup.string()
            .required(t('form.validation.required')),
        password_confirmation: Yup.string()
            .oneOf([Yup.ref('password')], t('form.validation.passwords_must_match'))
            .required(t('form.validation.required')),
});

const FormResetPassword = () => {

    const { t } = useTranslation();
    const { token } = useParams();
    const validationSchema = getValidationSchema(t);
    const { orderType } = useOrderTypeContext();
    const { setMainLoading, setMainLoadingIcon, setMainLoadingIconError, setMainLoadingTitle, setMainLoadingMessage, setMainLoadingCloseButton, setMainLoadingIconSuccess } = useLayoutContext();
    const { handleRedirect } = useLoginHook();


    const initialData = {
        password: '',
        password_confirmation: ''
    }

    const handleResetPassword = async (data: any) => {
        const payload = {
            password: data.password,
            password_confirmation: data.password_confirmation,
            token: token || ''
        }
        try {
            setMainLoading(true);
            setMainLoadingTitle(t('login.reset_password.loading_title'));
            setMainLoadingMessage(t('layout.loading_main.default_message'));

            const customerService = new CustomerService(orderType);
            const response = await customerService.passwordReset(payload);

            if (response.status === 204) {

                setMainLoadingIcon(false);
                setMainLoadingIconSuccess(true);
                setMainLoadingCloseButton(true);
                setMainLoadingTitle(t('login.reset_password.message_success_title'));
                setMainLoadingMessage(t('login.reset_password.message_success'));

                //* Redirect
                handleRedirect('');

            }
        } catch (error: any) {

            setMainLoadingIcon(false);
            setMainLoadingIconError(true);
            setMainLoadingCloseButton(true);
            if (error?.response?.data?.error === ERROR_PASSWORD_RESET_NOT_FOUND) {
                setMainLoadingTitle(t('login.reset_password.errors.password_reset_not_found_title'));
                setMainLoadingMessage('');
            } else {
                setMainLoadingTitle(t('errors.default_title'));
                setMainLoadingMessage(t('errors.default_message'));
            }
        } 
    }

    const isFieldRequired = (schema: any, fieldName: string) => {
        const field = schema.describe().fields[fieldName];
        return field && field.tests.some((test: any) => test.name === 'required');
    };

    return (
        <>
            <Formik
                initialValues={initialData}
                validationSchema={validationSchema}
                onSubmit={(data) => {
                    handleResetPassword(data);
                }}
                >
                {({ setFieldValue, values }) => (
                    <Form className="flex flex-col gap-6 justify-between">

                        <div className='h-full grid gap-3 p-4_ bg-gray-50_ rounded-xl_'>

                            <div className='flex flex-col gap-3'>
                                <div className='w-full text-left'>
                                    <span className='font-semibold text-shopfood-title'>
                                        {t('login.reset_password.new_password_label')}
                                        {isFieldRequired(validationSchema, 'password') && (<sup className="text-shopfood-error inline-flex ml-0.5"> <TbCircleFilled size={7} /></sup>)}
                                    </span>
                                    <input
                                        id="password"
                                        name="password"
                                        type='password'
                                        value={values.password}
                                        onChange={(e) => setFieldValue('password', e.target.value)}
                                        className="relative block w-full rounded border-0 p-2 ring-1 ring-gray-300 placeholder:text-gray-300 placeholder:font-normal font-bold focus:ring-2 focus:ring-gray-500 transition-all ease-in-out duration-200"
                                        autoFocus
                                    />
                                    <ErrorMessage name="password" component="div" className='text-xs text-shopfood-error pt-1' />
                                </div>

                                <div className='w-full text-left'>
                                    <span className='font-semibold text-shopfood-title'>
                                        {t('login.reset_password.confirm_password_label') || 'Confirme a nova senha'}
                                        {isFieldRequired(validationSchema, 'password_confirmation') && (<sup className="text-shopfood-error inline-flex ml-0.5"> <TbCircleFilled size={7} /></sup>)}
                                    </span>
                                    <input
                                        id="password_confirmation"
                                        name="password_confirmation"
                                        type='password'
                                        value={values.password_confirmation}
                                        onChange={(e) => setFieldValue('password_confirmation', e.target.value)}
                                        className="relative block w-full rounded border-0 p-2 ring-1 ring-gray-300 placeholder:text-gray-300 placeholder:font-normal font-bold focus:ring-2 focus:ring-gray-500 transition-all ease-in-out duration-200"
                                    />
                                    <ErrorMessage name="password_confirmation" component="div" className='text-xs text-shopfood-error pt-1' />
                                </div>

                            </div>
                        </div>

                        <div className='flex gap-2 flex-wrap'>
                            <ButtonPrimary type={'submit'} className='gap-1 flex-1 w-full' onClick={() => {}}>
                                <span>{t('login.reset_password.button_submit.label')}</span>
                            </ButtonPrimary>
                        </div>

                    </Form>
                )}
            </Formik>
        </>
    )
}

export default FormResetPassword;

